import { Title } from "react-head";
import {
  Link,
  Outlet,
} from "react-router-dom";
import { InformationCircleIcon } from "@heroicons/react/outline";

import ScrollToHeaderTitle from "misc/ScrollToHeaderTitle";
import UsageLineChart from "pages/analytics/MonitoringCharts";
import { RecentlyViewedProvider } from "providers/RecentlyViewed/RecentlyViewedProvider";
import {
  useOverviewData,
  useAnalyticsFilters,
} from "providers/AnalyticsProvider";
import DomainAnalyticsTable from "pages/analytics/domains/DomainAnalyticsTable";
import { TABLE_CONFIGS } from "utils/analytics/tableConfigs";
import ErrorLogsTable from "pages/analytics/error-logs/ErrorLogsTable";
import AnalyticsGlobalFilters from "pages/analytics/AnalyticsGlobalFilter";
import AnalyticsQuickStats from "pages/analytics/AnalyticsQuickStats";
import { useCounters } from "v2/hooks/billing/useCounters";
import MaxWidthPage from "components/MaxWidthPage";
import AnalyticsSection from "pages/analytics/shared/components/AnalyticsSection";
import { trackAnalyticsActionClicked } from "utils/Tracking";
import ActiveSubscription from "types/ActiveSubscription";
import { useAnalyticsDownload, useModalNavigation } from "hooks/analytics";
import { PureTooltip } from "components/Tooltip";
import { fmtDate, fmtShortNumber } from "utils";
import Spinner from "components/Spinner";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


function BillingCycleRenewalInline({
  activeSubscription,
}: {
  activeSubscription: ActiveSubscription | null | undefined;
}) {
  if (!activeSubscription) {
    return (
      <span className="text-neutral-500">
        <Spinner theme="secondary" size="XXS" className="inline mr-1" />
        Loading billing cycle...
      </span>
    );
  }

  const renewalDate = new Date(activeSubscription.nextRenewalAt * 1000);

  return (
    <span className="text-gray-500 whitespace-nowrap">
      (
      <Link
        to="/billing"
        className="hover:underline hover:text-gray-700 transition-colors"
      >
        Billing cycle renews on{" "}
        <span className="font-medium">
          {fmtDate(renewalDate, { weekday: undefined })}
        </span>
      </Link>
      )
    </span>
  );
}

function AnalyticsPageTitle({
  title,
  usedCredits,
  totalCredits,
}: {
  title: string;
  usedCredits: number;
  totalCredits: number;
}) {
  const { activeSubscription } = useActiveSubscription();

  return (
    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 py-6">
      <div className="text-2xl font-semibold">{title}</div>
      <div>
        <div className="border rounded py-1 px-3 inline-flex items-center gap-2 text-sm whitespace-nowrap max-w-full overflow-x-auto">
          <span className="flex items-center gap-1">
            <span className="font-bold">{fmtShortNumber(usedCredits)}</span>
            <span>/ {fmtShortNumber(totalCredits)} credits used</span>
          </span>

          {/* This billing cycle label is hidden on mobile */}
          <div className="hidden md:inline">
            <BillingCycleRenewalInline activeSubscription={ activeSubscription } />
          </div>

          <PureTooltip content="This is when your plan resets.">
            <InformationCircleIcon
              width={16}
              height={16}
              className="flex-shrink-0"
            />
          </PureTooltip>
        </div>
      </div>
    </div>
  );
}


const AnalyticsPage = () => {
  const { navigateToModal } = useModalNavigation();
  const {
    chartData,
    isOverviewLoading,
    error,
    overviewStatsSummary,
    refetchOverview,
    lastUpdatedTimestamp,
  } = useOverviewData();
  const { dateRange, filters } = useAnalyticsFilters();
  const { counters } = useCounters();

  const { download: downloadDomains, isDownloading: isDownloadingDomains } =
    useAnalyticsDownload();
  const { download: downloadErrors, isDownloading: isDownloadingErrors } =
    useAnalyticsDownload();

  const handleDownloadDomains = () => {
    downloadDomains({
      dateRange,
      filters,
      filenamePrefix: "domain-analytics",
      trackingEvent: "domain_analytics",
    });
  };

  const handleDownloadErrors = () => {
    downloadErrors({
      dateRange,
      filters,
      filenamePrefix: "error-logs",
      type: "error-logs",
      // Two-tier rule: the overview download matches what the overview shows —
      // date range + global filters + failures only. Error-log filters apply
      // only to the error-logs page's own download, where they're visible.
      extraParams: { status: "failure" },
      trackingEvent: "error_logs",
    });
  };

  const handleCustomizeColumns = (type: 'domain' | 'error') => {
    if (type === 'domain') {
      navigateToModal("/analytics/customize-columns", {
        tableHeaders: TABLE_CONFIGS.domain.headers,
        tableType: "domain",
      });
      trackAnalyticsActionClicked({
        section: "overview",
        action: "customize_columns",
        target: "domain_analytics_table",
      });
    } else {
      navigateToModal("/analytics/customize-columns", {
        tableHeaders: TABLE_CONFIGS.error.headers,
        tableType: "error",
      });
      trackAnalyticsActionClicked({
        section: "overview",
        action: "customize_columns",
        target: "error_logs_table",
      });
    }
  }

  const usedCredits = (counters?.requestCount ?? 0) + (counters?.payAsYouGoCredits ?? 0);
  const totalCredits = counters?.totalCredits ?? 0;

  return (
    <>
      <MaxWidthPage fullWidth>
        <Title>ScraperAPI | Analytics Overview</Title>
        <ScrollToHeaderTitle />
        <div className="px-2 md:px-8 dark:md:px-6 flex flex-col gap-4 mb-6">
          <AnalyticsPageTitle
            title="Analytics Overview"
            usedCredits={usedCredits}
            totalCredits={totalCredits}
          />
          <RecentlyViewedProvider>
            <div className="flex gap-4 flex-wrap">
              <AnalyticsGlobalFilters />
            </div>
            <UsageLineChart chartData={chartData} isLoading={isOverviewLoading} error={error} refetch={refetchOverview} lastUpdated={lastUpdatedTimestamp} />
            <AnalyticsQuickStats
              stats={overviewStatsSummary}
              isLoading={isOverviewLoading}
              error={error}
            />
            {/* Domain analytics section */}
            <AnalyticsSection
              title="Domain analytics"
              onCustomizeColumns={() => handleCustomizeColumns('domain')}
              onDownload={handleDownloadDomains}
              isDownloading={isDownloadingDomains}
              downloadButtonText="DOWNLOAD REPORT"
            >
              <DomainAnalyticsTable mode="overview" />
            </AnalyticsSection>

            {/* Error Logs Section */}
            <AnalyticsSection
              title="Error logs"
              onCustomizeColumns={() => handleCustomizeColumns('error')}
              onDownload={handleDownloadErrors}
              isDownloading={isDownloadingErrors}
              downloadButtonText="DOWNLOAD LOGS"
            >
              <ErrorLogsTable mode="overview" />
            </AnalyticsSection>
          </RecentlyViewedProvider>
        </div>
      </MaxWidthPage>
      <Outlet />
    </>
  );
};

export default AnalyticsPage;
